import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import {
  AmbulatoryAuthorizationDashboardContext,
  Material
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import CardNew from 'presentation/shared/components/CardNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { validationSchema } from './validationSchema'
import { MaterialCard } from 'presentation/doctor/components/Cards/AmbulatoryMaterialCard'

type LocationProps = {
  isEditing?: boolean
}

const AmbulatoryMaterialsLayout = () => {
  const history = useHistory()
  const stateLocation = useLocation()?.state as LocationProps
  const { state, dispatch } = useContext<any>(
    AmbulatoryAuthorizationDashboardContext
  )
  const { patient, materials } = state
  const [selectedMaterialsList, setSelectedMaterialsList] = useState<
    Material[]
  >([])

  if (!patient?.cpf) {
    history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
  }

  const formik = useFormik({
    initialValues: {
      material: '',
      quantity: ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      addMaterial(values)
    }
  })

  const addMaterial = (values: any) => {
    const isDuplicate = selectedMaterialsList.some(
      (material: Material) => material.name === values.material
    )

    if (isDuplicate) {
      toast.error('Este material já foi adicionado.')
      return
    }

    const list = [...selectedMaterialsList]

    list.push({
      name: values.material,
      quantity: values.quantity,
      isMain: list.length === 0
    })

    setSelectedMaterialsList(list)
    formik.resetForm()
  }

  const removeMaterial = (name: string) => {
    const updatedList = selectedMaterialsList.filter(
      (material: Material) => material.name !== name
    )
    setSelectedMaterialsList(updatedList)
  }

  const handleInputQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    formik.setFieldValue('quantity', value)
  }

  const handleInputQuantityBlur = (e: ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value)

    if (value <= 0) {
      value = 1
    }

    formik.setFieldValue('quantity', value)
  }

  const handleQuantityChange = (
    name: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedMaterialsList]
    const newQuantity = e.target.value

    const newList = list.map((material) =>
      material.name === name ? { ...material, quantity: newQuantity } : material
    )
    setSelectedMaterialsList(newList)
  }

  const handleQuantityBlur = (
    name: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const list = [...selectedMaterialsList]
    let newQuantity = Number(e.target.value)

    if (newQuantity <= 0) {
      newQuantity = 1
    }

    const newList = list.map((material) =>
      material.name === name ? { ...material, quantity: newQuantity } : material
    )
    setSelectedMaterialsList(newList)
  }

  function handleSubmit() {
    dispatch({
      type: 'MATERIALS',
      payload: selectedMaterialsList
    })
    if (stateLocation?.isEditing) {
      history.push('resumo#mat')
    } else {
      history.push('observacoes')
    }
  }

  useEffect(() => {
    if (materials?.length) {
      setSelectedMaterialsList(materials)
    }
  }, [])

  return (
    <>
      <HeaderNew
        actualPageOnClick={() =>
          stateLocation?.isEditing ? history.push('resumo') : history.goBack()
        }
        actualPageTitle="Solicitar pedido ambulatorial"
      />

      <ContainerNew
        title="Materiais"
        titleColor="neutral800"
        titleSize="large"
        titleWeight="bold"
        subtitle="Inclua os materiais que serão utilizado e suas quantidades."
        // style={{ paddingBottom: 0 }}
        primaryButton={
          <ButtonNew fullWidth size="large" onClick={handleSubmit}>
            {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
          </ButtonNew>
        }
      >
        <S.Wrapper>
          <S.FormWrapper onSubmit={formik.handleSubmit}>
            <TextFieldNew
              labelOverlapping
              label="Material "
              placeholder="Digite o nome do material"
              labelSize="xxsmall"
              name="material"
              value={formik.values.material}
              onChange={formik.handleChange}
              required
              requiredColor="neutral800"
              error={formik.touched.material && formik.errors.material}
            />

            <TextFieldNew
              labelOverlapping
              label="Quantidade "
              placeholder="Digite ou selecione"
              labelSize="xxsmall"
              type="number"
              name="quantity"
              value={formik.values.quantity}
              onChange={(e) => handleInputQuantityChange(e)}
              onBlur={(e) => handleInputQuantityBlur(e)}
              min={1}
              required
              requiredColor="neutral800"
              error={formik.touched.quantity && formik.errors.quantity}
              inputMode="numeric"
              pattern="[0-9]*"
            />

            <ButtonNew
              type="submit"
              size="large"
              outlined
              disabled={!formik.isValid}
            >
              Incluir material
            </ButtonNew>
          </S.FormWrapper>

          <DividerNew marginTop="24px" marginBottom="24px" />

          <HeadingNew
            as="h6"
            weight="semiBold"
            color="neutral600"
            size="medium"
            style={{ marginBottom: '24px' }}
          >
            Materiais incluídos
          </HeadingNew>

          {selectedMaterialsList?.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                paddingBottom: '24px'
              }}
            >
              {selectedMaterialsList.map(
                (material: Material, index: number) => {
                  return (
                    <MaterialCard
                      key={index}
                      handleQuantityChange={handleQuantityChange}
                      handleQuantityBlur={handleQuantityBlur}
                      removeMaterial={removeMaterial}
                      material={material}
                    />
                  )
                }
              )}
            </div>
          ) : (
            <>
              <CardNew
                padding="24px"
                background="neutral50"
                shadow="none"
                border="neutral200"
              >
                <SupportTextNew
                  color="neutral500"
                  size="small"
                  weight="semiBold"
                >
                  Você ainda não incluiu nenhum material
                </SupportTextNew>
              </CardNew>
            </>
          )}
        </S.Wrapper>
      </ContainerNew>
    </>
  )
}

export default AmbulatoryMaterialsLayout
